import React, { useEffect, useRef } from 'react';
import {
  Box, Checkbox, Chip, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { asArray, formatDate, initials, isBlank, scoreColors } from '../utils';

const headSx = {
  bgcolor: '#f8fafc',
  color: '#64748b',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  py: 1,
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
};

const tagSx = { height: 22, fontSize: '0.7rem', fontWeight: 600, borderRadius: '6px', flexShrink: 0 };

/** Chips list; `nowrap` keeps it on one line (table rows), the rest goes in the "+n" tooltip. */
export const TagList = ({ items, max = 3, color = '#03407b', bg = '#ebf4fb', nowrap = false }) => {
  const list = asArray(items);
  if (list.length === 0) return <Typography sx={{ fontSize: '0.78rem', color: '#cbd5e1' }}>—</Typography>;
  return (
    <Box sx={{ display: 'flex', flexWrap: nowrap ? 'nowrap' : 'wrap', gap: 0.5, overflow: 'hidden', minWidth: 0 }}>
      {list.slice(0, max).map((t, i) => (
        <Chip key={`${t}-${i}`} label={t} size="small" sx={{ ...tagSx, bgcolor: bg, color, maxWidth: nowrap ? 130 : 200, flexShrink: nowrap ? 1 : 0 }} />
      ))}
      {list.length > max && (
        <Tooltip title={list.slice(max).join(', ')}>
          <Chip label={`+${list.length - max}`} size="small" sx={{ ...tagSx, bgcolor: '#f1f5f9', color: '#64748b' }} />
        </Tooltip>
      )}
    </Box>
  );
};

export const StatusChips = ({ candidate }) => (
  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
    <Chip
      size="small"
      label={candidate.viewed ? 'Consulté' : 'Nouveau'}
      sx={{ ...tagSx, bgcolor: candidate.viewed ? '#f1f5f9' : '#eff6ff', color: candidate.viewed ? '#64748b' : '#2563eb' }}
    />
    {candidate.added_to_crm && <Chip size="small" label="CRM" sx={{ ...tagSx, bgcolor: '#f0fdf4', color: '#16a34a' }} />}
  </Box>
);

export const ScoreCell = ({ score }) => {
  if (typeof score !== 'number' || Number.isNaN(score)) return <Typography sx={{ fontSize: '0.78rem', color: '#cbd5e1' }}>—</Typography>;
  const sc = scoreColors(score);
  const value = Math.max(0, Math.min(10, score));
  return (
    <Tooltip title="Pertinence par rapport à votre recherche (sur 10)">
      <Box sx={{ width: 64 }}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: sc.color, lineHeight: 1.1 }}>
          {value.toFixed(1)}
          <Box component="span" sx={{ fontSize: '0.66rem', fontWeight: 600, color: '#94a3b8' }}>/10</Box>
        </Typography>
        <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: '#eef2f7', overflow: 'hidden' }}>
          <Box sx={{ width: `${value * 10}%`, height: '100%', bgcolor: sc.color, borderRadius: 2 }} />
        </Box>
      </Box>
    </Tooltip>
  );
};

const subtitleOf = (c) => {
  const role = asArray(c.roles_held)[0];
  const place = isBlank(c.location) ? null : c.location;
  return [role, place].filter(Boolean).join(' · ') || (isBlank(c.email) ? '—' : c.email);
};

const CandidateList = ({
  candidates, mode, loading, selectedId, selectedIds, showArchived, compact,
  onSelect, onToggleSelect, onToggleAll, onArchive, onRestore, onDelete,
}) => {
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!selectedId || !bodyRef.current) return;
    const row = bodyRef.current.querySelector(`[data-row-id="${selectedId}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  if (loading && candidates.length === 0) {
    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} variant="rounded" height={44} />)}
      </Box>
    );
  }

  if (candidates.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
        <Box sx={{ width: 52, height: 52, borderRadius: '14px', bgcolor: '#ebf4fb', color: '#03407b', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
          {mode === 'search' ? <SearchOffIcon /> : <MarkEmailUnreadOutlinedIcon />}
        </Box>
        <Typography sx={{ fontWeight: 800, color: '#0f172a' }}>
          {mode === 'search' ? 'Aucun résultat' : showArchived ? 'Aucun candidat archivé' : 'Aucun candidat sur cette période'}
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.5, maxWidth: 380 }}>
          {mode === 'search'
            ? 'Essayez une autre formulation, par ex. « développeur React 5 ans Paris ».'
            : showArchived
              ? 'Les candidats archivés apparaîtront ici.'
              : 'Élargissez la période ou cliquez sur « Récupérer les candidats » pour analyser les nouveaux CV de la boîte mail.'}
        </Typography>
      </Box>
    );
  }

  const allChecked = candidates.every((c) => selectedIds.has(c.id));
  const someChecked = !allChecked && candidates.some((c) => selectedIds.has(c.id));
  const search = mode === 'search';

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0 }}>
      <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: compact ? 560 : 820 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headSx, width: 44, px: 1 }}>
              <Checkbox size="small" checked={allChecked} indeterminate={someChecked} onChange={() => onToggleAll(!allChecked)} />
            </TableCell>
            {search && <TableCell sx={{ ...headSx, width: 84 }}>Score</TableCell>}
            <TableCell sx={{ ...headSx, width: compact ? '46%' : '30%' }}>Candidat</TableCell>
            <TableCell sx={headSx}>Compétences clés</TableCell>
            {!compact && <TableCell sx={{ ...headSx, width: 96 }}>Reçu le</TableCell>}
            <TableCell sx={{ ...headSx, width: compact ? 92 : 124 }}>Statut</TableCell>
            <TableCell sx={{ ...headSx, width: showArchived ? 80 : 48 }} />
          </TableRow>
        </TableHead>
        <TableBody ref={bodyRef}>
          {candidates.map((c) => {
            const selected = selectedId === c.id;
            const skills = [...asArray(c.technical_skills), ...asArray(c.programming_languages)];
            return (
              <TableRow
                key={c.id}
                data-row-id={c.id}
                hover
                onClick={() => onSelect(c)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selected ? '#f0f7fd !important' : 'transparent',
                  boxShadow: selected ? 'inset 3px 0 0 #03407b' : 'none',
                  '& td': { borderBottom: '1px solid #f1f5f9', py: 0.9 },
                  '&:hover .row-actions': { opacity: 1 },
                }}
              >
                <TableCell sx={{ px: 1 }} onClick={(e) => e.stopPropagation()}>
                  <Checkbox size="small" checked={selectedIds.has(c.id)} onChange={() => onToggleSelect(c.id)} />
                </TableCell>
                {search && <TableCell><ScoreCell score={c.score} /></TableCell>}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                      <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: selected ? '#03407b' : '#ebf4fb', color: selected ? '#fff' : '#03407b', fontSize: '0.72rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                        {initials(c.full_name)}
                      </Box>
                      {!c.viewed && (
                        <Tooltip title="Pas encore consulté">
                          <Box sx={{ position: 'absolute', top: -2, right: -2, width: 10, height: 10, borderRadius: '50%', bgcolor: '#2563eb', border: '2px solid #fff' }} />
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: '0.86rem', fontWeight: c.viewed ? 600 : 800, color: '#0f172a' }}>
                        {c.full_name || 'Sans nom'}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: '0.74rem', color: '#64748b' }}>{subtitleOf(c)}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><TagList items={skills} max={compact ? 2 : 3} nowrap /></TableCell>
                {!compact && <TableCell sx={{ fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(c.date)}</TableCell>}
                <TableCell><StatusChips candidate={c} /></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()} sx={{ pr: 1 }}>
                  <Box className="row-actions" sx={{ display: 'flex', justifyContent: 'flex-end', opacity: selected ? 1 : 0.35, transition: 'opacity .15s' }}>
                    {showArchived ? (
                      <>
                        <Tooltip title="Restaurer">
                          <IconButton size="small" onClick={() => onRestore(c.id)} sx={{ color: '#03407b' }}>
                            <UnarchiveOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer définitivement">
                          <IconButton size="small" onClick={() => onDelete(c.id)} sx={{ color: '#dc2626' }}>
                            <DeleteOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Archiver">
                        <IconButton size="small" onClick={() => onArchive(c.id)} sx={{ color: '#64748b', '&:hover': { color: '#03407b', bgcolor: '#ebf4fb' } }}>
                          <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CandidateList;
